// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import {
  Edit,
  Trash2,
  CheckCircle,
  User,
  Building2,
  Hash,
  Truck,
  FileText,
  DoorOpen,
} from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, Badge, EmptyState, Skeleton, Modal } from '@/components/ui'
import {
  useParcel,
  useDeleteParcel,
  useCollectParcel,
} from '@/hooks/useParcels'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import { ExtractionStatusChip } from './ExtractionStatusChip'
import { useParcelExtractionEnabled } from '@/hooks/useParcelExtractionEnabled'
import { getParcelDisplayStatus } from '@/lib/parcelStatus'

export function ParcelDetailPage() {
  const { t } = useTranslation('parcels')
  const { t: tc } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const parcelId = Number(id)
  const navigate = useNavigate()
  const formatDate = useDateFormatter()
  const parcelTextExtractionEnabled = useParcelExtractionEnabled()

  const { data: parcel, isLoading, isFetching } = useParcel(parcelId)
  const deleteParcel = useDeleteParcel()
  const collectParcel = useCollectParcel()

  const [showDeleteModal, setShowDeleteModal] = useState(false)
  // Local controlled state for the candidate unit selection.
  // Clicking a candidate chip fills this without saving — the user must
  // explicitly navigate to the edit page to persist the choice.
  const [selectedUnitHint, setSelectedUnitHint] = useState<string | null>(null)

  const handleDelete = async () => {
    await deleteParcel.mutateAsync(parcelId)
    navigate('/parcels')
  }

  const handleCollect = async () => {
    await collectParcel.mutateAsync(parcelId)
  }

  const getStatusBadge = () => {
    if (!parcel) return null
    // EP-67: show the derived status (surfaces "Needs review") when the dual-gate
    // is on; otherwise the plain lifecycle status.
    if (parcelTextExtractionEnabled) {
      const s = getParcelDisplayStatus(parcel)
      return <Badge variant={s.variant}>{t(s.labelKey, s.labelOpts)}</Badge>
    }
    switch (parcel.status) {
      case 'collected':
        return <Badge variant="success">{t('status.collected')}</Badge>
      case 'notified':
        return <Badge variant="info">{t('status.notified')}</Badge>
      case 'pending':
      default:
        return <Badge variant="warning">{t('status.pending')}</Badge>
    }
  }

  if (isLoading) {
    return (
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <Card>
            <Skeleton className="h-48" />
          </Card>
        </div>
    )
  }

  if (!parcel) {
    return (
        <Card>
          <EmptyState
            title={t('empty.title')}
            action={
              <Button onClick={() => navigate('/parcels')}>
                {t('title')}
              </Button>
            }
          />
        </Card>
    )
  }

  const breadcrumbLabel = parcel.tracking_number || `Parcel #${parcel.id}`

  return (
    <>
      <div className="space-y-6">
        <Breadcrumbs
          items={[
            { label: t('title'), href: '/parcels' },
            { label: breadcrumbLabel },
          ]}
        />

        <PageHeader
          title={breadcrumbLabel}
          isRefreshing={isFetching && !isLoading}
          // EP-106 Item 1 — primary is Mark collected while pending; once
          // collected there's no lifecycle action left, so Edit promotes.
          primaryAction={
            parcel.status === 'pending'
              ? {
                  key: 'mark-collected',
                  label: t('actions.mark_collected'),
                  icon: CheckCircle,
                  onClick: handleCollect,
                  loading: collectParcel.isPending,
                }
              : undefined
          }
          overflowActions={[
            {
              key: 'edit',
              label: tc('actions.edit'),
              icon: Edit,
              to: `/parcels/${id}/edit`,
            },
            {
              key: 'delete',
              label: tc('actions.delete'),
              icon: Trash2,
              onClick: () => setShowDeleteModal(true),
              destructive: true,
            },
          ]}
        />

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Info Grid */}
          <Card className="lg:col-span-2">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold">{t('parcel_detail')}</h2>
              {getStatusBadge()}
            </div>

            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60 flex items-center gap-1">
                  <Hash className="w-4 h-4" /> {t('labels.tracking_number')}
                </dt>
                <dd className="font-medium break-words">
                  {parcel.tracking_number || '-'}
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60 flex items-center gap-1">
                  <Truck className="w-4 h-4" /> {t('labels.carrier')}
                </dt>
                <dd className="font-medium break-words">{parcel.carrier || '-'}</dd>
              </div>
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60 flex items-center gap-1">
                  <User className="w-4 h-4" /> {t('labels.customer')}
                </dt>
                <dd className="font-medium break-words">
                  {parcel.customer?.name || '-'}
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60 flex items-center gap-1">
                  <Building2 className="w-4 h-4" /> {t('labels.property')}
                </dt>
                <dd className="font-medium break-words">
                  {parcel.property?.name || '-'}
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60 flex items-center gap-1">
                  <DoorOpen className="w-4 h-4" /> {t('labels.unit')}
                </dt>
                <dd className="font-medium">
                  <div className="flex flex-wrap items-center gap-2">
                    <span data-testid="unit-display">
                      {selectedUnitHint ??
                        parcel.unit?.number ??
                        parcel.unit?.name ??
                        (parcel.extraction_status === 'matched'
                          ? (parcel.extracted_unit_number ?? '-')
                          : '-')}
                    </span>
                    <ExtractionStatusChip
                      status={parcel.extraction_status}
                      extractedUnitNumber={parcel.extracted_unit_number}
                    />
                  </div>
                  {parcel.extraction_status === 'needs_review' &&
                    parcel.extraction_candidates &&
                    parcel.extraction_candidates.length > 0 && (
                      <div
                        className="flex flex-wrap gap-2 mt-2"
                        data-testid="extraction-candidates"
                        aria-label={t('extraction.confirm_unit')}
                      >
                        {parcel.extraction_candidates.map((candidate) => (
                          <Button
                            key={candidate}
                            variant="outline"
                            size="xs"
                            className="flex-shrink-0 whitespace-nowrap"
                            data-testid={`candidate-${candidate}`}
                            onClick={() => setSelectedUnitHint(candidate)}
                          >
                            {candidate}
                          </Button>
                        ))}
                      </div>
                    )}
                </dd>
              </div>
              {parcel.description && (
                <div className="col-span-full min-w-0">
                  <dt className="text-sm text-base-content/60 flex items-center gap-1">
                    <FileText className="w-4 h-4" /> {t('labels.description')}
                  </dt>
                  <dd className="font-medium break-words">{parcel.description}</dd>
                </div>
              )}
            </dl>
          </Card>

          {/* Timeline */}
          <Card>
            <h2 className="text-lg font-semibold mb-4">{t('timeline.received')}</h2>
            <ul className="timeline timeline-vertical timeline-compact">
              {/* Received */}
              <li>
                <div className="timeline-start text-xs text-base-content/60">
                  {formatDate(parcel.received_at)}
                </div>
                <div className="timeline-middle">
                  <div className="w-4 h-4 rounded-full bg-success" />
                </div>
                <div className="timeline-end timeline-box">
                  {t('timeline.received')}
                </div>
                <hr className={parcel.collected_at ? 'bg-success' : 'bg-base-300'} />
              </li>

              {/* Collected */}
              <li>
                <hr className={parcel.collected_at ? 'bg-success' : 'bg-base-300'} />
                <div className="timeline-start text-xs text-base-content/60">
                  {parcel.collected_at
                    ? formatDate(parcel.collected_at)
                    : '-'}
                </div>
                <div className="timeline-middle">
                  <div
                    className={`w-4 h-4 rounded-full ${
                      parcel.collected_at ? 'bg-success' : 'bg-base-300'
                    }`}
                  />
                </div>
                <div
                  className={`timeline-end timeline-box ${
                    !parcel.collected_at ? 'opacity-40' : ''
                  }`}
                >
                  {t('timeline.collected')}
                </div>
              </li>
            </ul>
          </Card>
        </div>

        {/* Photo Gallery */}
        {parcel.image_urls && parcel.image_urls.length > 0 && (
          <Card>
            <h2 className="text-lg font-semibold mb-4">{t('labels.photos')}</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {parcel.image_urls.map((url, index) => {
                const imageUrl = url.startsWith('/api/images/')
                  ? `${import.meta.env.VITE_API_URL || ''}${url}`
                  : `${import.meta.env.VITE_API_URL || ''}/api/images/${url}`
                return (
                  <a
                    key={index}
                    href={imageUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-lg overflow-hidden border border-base-200 hover:border-primary transition-colors"
                  >
                    <img
                      src={imageUrl}
                      alt={`${t('labels.photos')} ${index + 1}`}
                      className="w-full h-32 object-cover"
                    />
                  </a>
                )
              })}
            </div>
          </Card>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title={tc('actions.delete')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowDeleteModal(false)}>
              {tc('actions.cancel')}
            </Button>
            <Button
              variant="error"
              onClick={handleDelete}
              loading={deleteParcel.isPending}
            >
              {tc('actions.delete')}
            </Button>
          </>
        }
      >
        <p>{t('messages.delete_confirm')}</p>
      </Modal>
    </>
  )
}
